<template>
  <hr-form-dialog
    ref="dialog"
    size="lg"
    :title="formTitle"
    data-e2e="dialog-edit-user"
    @submit="onSubmit"
    @hide="onDialogHide"
  >
    <banner
      v-if="readOnly && readOnlyMessage"
      class="q-mb-md"
      :message="readOnlyMessage"
    />

    <hr-tabs
      v-model="tab"
      class="col-12 q-mb-md"
    >
      <hr-tab
        v-for="tabItem in modalTabs"
        :key="tabItem.name"
        :name="tabItem.name"
        :label="tabItem.label"
        :data-e2e="`tab-user-${tabItem.name}`"
      />
    </hr-tabs>
    <hr-tab-panels
      v-model="tab"
      keep-alive
    >
      <hr-tab-panel
        name="profile"
        class="q-px-none"
      >
        <banner
          v-if="!hasAccessToStaffMember"
          class="q-mb-xl q-mt-lg"
          :message="$t('staff.modals.editStaffMember.bannerNoStaffAccessInfo')"
        />

        <banner
          v-if="showDisabledUserBanner"
          type="error"
          :message="$t('users.modals.editUser.userIsDisabled')"
          class="q-mb-xl q-mt-lg"
        >
          <template #action>
            <q-btn
              flat
              :label="$t('settings.users.listItemTextEnable')"
              @click="onEnableUser"
            />
          </template>
        </banner>

        <template v-if="showLicenseBanner">
          <banner
            v-if="isStandardLicense"
            class="q-mb-xl q-mt-lg"
            :message="$t('staff.modals.editStaffMember.bannerTitleStandardLicense')"
          >
            <template #action>
              <q-btn
                flat
                :label="$t('staff.modals.editStaffMember.btnUpgradeToPremium')"
                @click="onUpgradeToPremium"
              />
            </template>
          </banner>
          <banner
            v-if="isPremiumLicense"
            class="q-mb-xl q-mt-lg"
            :message="$t('staff.modals.editStaffMember.bannerTitlePremiumLicense')"
          >
            <template #action>
              <q-btn
                flat
                :label="$t('common.buttonUndo')"
                @click="onDowngradeToStandard"
              />
            </template>
          </banner>
        </template>

        <div class="row q-col-gutter-md">
          <div
            class="col-xs-12 col-md-3 col-lg-2 flex justify-center"
          >
            <q-avatar
              size="125px"
              class="q-mr-sm"
            >
              <q-img :src="clientPhoto" />
              <input
                ref="file"
                accept="image/*"
                type="file"
                class="file-overlay cursor-pointer hidden"
                :disabled="readOnly || !hasAccessToStaffMember"
                @input="onSelectUploadedFile"
              >
              <div
                class="file-overlay staff-photo-overlay cursor-pointer"
                @click="$refs.file.click()"
              >
                <q-icon
                  name="photo_camera"
                  color="white"
                  size="lg"
                />
              </div>

              <q-btn
                flat
                round
                size="12px"
                icon="more_vert"
                class="staff-avatar-actions"
                @click.stop
              >
                <q-menu>
                  <q-list>
                    <q-item
                      v-ripple
                      v-close-popup
                      clickable
                      :disable="!hasAccessToStaffMember"
                      @click="$refs.file.click()"
                    >
                      <q-item-section avatar>
                        <q-icon name="photo_camera" />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('staff.modals.editStaffMember.btnSetNewPhoto') }}
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="selectedStaffPhoto || editStaffMember.photo"
                      v-ripple
                      v-close-popup
                      clickable
                      :disable="readOnly"
                      @click="onPhotoDelete"
                    >
                      <q-item-section avatar>
                        <q-icon name="delete" />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('common.buttonDelete') }}
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </q-avatar>
          </div>
          <div class="col-xs-12 col-md-9 col-lg-10">
            <div class="row q-col-gutter-md main-fields">
              <q-input
                v-model="staffMemberFirstName"
                :autofocus="isCreatingNewUser"
                :outlined="$q.screen.lt.md"
                hide-bottom-space
                :label="$t('staff.modals.editStaffMember.inputClientFirstNameLabel')"
                :disable="readOnly || !hasAccessToStaffMember"
                class="col-xs-12 col-sm-6"
              />
              <q-input
                v-model="staffMemberLastName"
                :outlined="$q.screen.lt.md"
                hide-bottom-space
                :label="$t('staff.modals.editStaffMember.inputClientLastNameLabel')"
                :disable="readOnly || !hasAccessToStaffMember"
                class="col-xs-12 col-sm-6"
              />
              <q-input
                ref="clientName"
                v-model="editStaffMember.clientName"
                :outlined="$q.screen.lt.md"
                hide-bottom-space
                :rules="[
                  val => !!val || $t('staff.modals.editStaffMember.inputClientNameValidator')
                ]"
                :label="$t('staff.modals.editStaffMember.inputClientLegalNameLabel') + '*'"
                :disable="readOnly || !hasAccessToStaffMember"
                class="col-xs-12 col-sm-6"
                data-e2e="input-full-name"
              />

              <template v-if="hasStaffMemberSystemDataFields">
                <custom-data-field
                  v-for="dataField in staffMemberSystemDataFields"
                  :key="dataField.id"
                  :model-value="getStaffMemberData(dataField)"
                  :data-field="dataField"
                  :client-id="editStaffMember.id"
                  :disable="readOnly || !hasAccessToStaffMember"
                  :outlined="$q.screen.lt.md"
                  :clearable="!readOnly && hasAccessToStaffMember && !dataField.allowMultipleOptions"
                  class="col-xs-12 col-sm-6"
                  @update:model-value="setStaffMemberData(dataField.id, $event)"
                />
              </template>

              <q-input
                ref="name"
                v-model="editUser.name"
                lazy-rules
                :xautofocus="isCreatingNewUser"
                :outlined="$q.screen.lt.md"
                hide-bottom-space
                :label="$t('settings.inputUsernameLabel') + '*'"
                :rules="[
                  val => !!val || $t('settings.inputUsernameValidator'),
                  val => (!val || val.trim().length > 3) || $t('settings.inputUsernameValidator')
                ]"
                class="col-12"
                data-e2e="input-username"
              />
            </div>
          </div>

          <div class="col-sm-9 col-12 text-subtitle1 text-weight-medium q-mt-md">
            {{ $t('staff.modals.editStaffMember.formSectionHeaderContact') }}
          </div>

          <q-input
            ref="email"
            v-model.trim="editUser.email"
            type="email"
            outlined
            hide-bottom-space
            :label="$t('users.modals.editUser.inputEmailLabel') + '*'"
            :disable="disableEmail || readOnly"
            :rules="[
              val => isValidEmail(val) || $t('users.modals.editUser.inputEmailValidator'),
              uniqueEmail,
            ]"
            lazy-rules
            class="col-6"
            data-e2e="input-email"
          />
          <q-input
            v-model="editStaffMember.phone"
            class="col-6"
            :label="$t('staff.modals.editStaffMember.inputClientPhoneLabel')"
            :disable="readOnly || !hasAccessToStaffMember"
            outlined
          />

          <country-select
            ref="country"
            v-model="editStaffMember.country"
            show-flag
            :rules="[
              val => !!val || $t('staff.modals.editStaffMember.inputClientCountryValidator')
            ]"
            :label="$t('staff.modals.editStaffMember.inputClientCountryLabel')"
            :disable="readOnly || !hasAccessToStaffMember"
            emit-value
            map-options
            outlined
            :class="{'col-sm-6': countryHasStates(editStaffMember.country)}"
            class="col-xs-12"
            hide-bottom-space
          />
          <q-select
            v-if="countryHasStates(editStaffMember.country)"
            v-model="editStaffMember.state"
            :options="countryStates(editStaffMember.country)"
            option-value="code"
            emit-value
            map-options
            outlined
            hide-bottom-space
            :label="countryStateLabel(editStaffMember.country)"
            :disable="readOnly || !hasAccessToStaffMember"
            class="col-xs-12 col-sm-6"
          />
          <q-input
            v-model="editStaffMember.address1"
            outlined
            :label="$t('staff.modals.editStaffMember.inputClientAddress1Label')"
            :disable="readOnly || !hasAccessToStaffMember"
            class="col-12"
          />
          <q-input
            v-model="editStaffMember.address2"
            :label="$t('staff.modals.editStaffMember.inputClientAddress2Label')"
            :disable="readOnly || !hasAccessToStaffMember"
            outlined
            class="col-12"
          />
          <q-input
            v-if="countryHasPostalCode(editStaffMember.country)"
            v-model="editStaffMember.postalCode"
            :label="countryPostalCodeLabel(editStaffMember.country)"
            :disable="readOnly || !hasAccessToStaffMember"
            outlined
            class="col-xs-12 col-sm-6"
          />
          <q-input
            v-model="editStaffMember.city"
            :label="$t('staff.modals.editStaffMember.inputClientCityLabel')"
            outlined
            :disable="readOnly || !hasAccessToStaffMember"
            :class="{'col-sm-6':countryHasPostalCode(editStaffMember.country)}"
            class="col-xs-12"
          />

          <div class="col-sm-9 col-12 text-subtitle1 text-weight-medium q-mt-md">
            {{ $t('staff.modals.editStaffMember.formSectionHeaderPersonal') }}
          </div>

          <q-select
            v-model="editStaffMember.gender"
            :options="genderList"
            emit-value
            map-options
            clearable
            class="col-xs-12 col-sm-6"
            :label="$t('staff.modals.editStaffMember.inputClientGenderLabel')"
            :disable="readOnly || !hasAccessToStaffMember"
            outlined
          />
          <q-input
            v-model="editStaffMember.reference"
            class="col-xs-12 col-sm-6"
            :label="$t('staff.modals.editStaffMember.inputClientReferenceIDLabel')"
            :disable="readOnly || !hasAccessToStaffMember"
            outlined
          />
          <date-input
            v-model="staffMemberBirthDate"
            :label="$t('staff.modals.editStaffMember.inputClientDOBLabel')"
            outlined
            :disable="readOnly || !hasAccessToStaffMember"
            :options="maxBirthDate"
            class="col-6"
          />
          <q-input
            :model-value="staffMemberAge"
            :label="$t('staff.modals.editStaffMember.inputClientAgeLabel')"
            readonly
            outlined
            :disable="!hasAccessToStaffMember"
            class="col-6"
          />

          <div class="col-sm-9 col-12 text-subtitle1 text-weight-medium q-mt-md">
            {{ $t('staff.modals.editStaffMember.formSectionHeaderAdditionalInfo') }}
          </div>

          <q-select
            ref="units"
            v-model="editUser.units"
            :options="unitsList"
            :label="$t('users.modals.editUser.selectUnitsLabel')"
            :hint="unitsHint"
            :rules="unitsRules"
            :disable="readOnly"
            multiple
            use-chips
            hide-bottom-space
            emit-value
            map-options
            outlined
            class="col-12"
          >
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
              >
                <q-item-section>
                  <!--eslint-disable-next-line vue/no-v-text-v-html-on-component-->
                  <q-item-label v-html="scope.opt.label" />
                  <q-item-label caption>
                    {{ scope.opt.hint }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <q-select
            v-if="hasUnits"
            v-model="selectedStaffMemberUnit"
            :options="availableUnitsList"
            :label="$t('staff.modals.editStaffMember.inputClientUnitLabel')"
            :disable="readOnly || !hasAccessToStaffMember"
            clearable
            outlined
            emit-value
            map-options
            class="col-12"
          >
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
              >
                <q-item-section>
                  <!--eslint-disable-next-line vue/no-v-text-v-html-on-component-->
                  <q-item-label v-html="scope.opt.label" />
                  <q-item-label caption>
                    {{ scope.opt.hint }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <!-- Staff only information -->
          <template v-if="showStaffFields">
            <div class="col-sm-9 col-12 text-subtitle1 text-weight-medium q-mt-md">
              {{ $t('staff.modals.editStaffMember.formSectionHeaderStaff') }}
            </div>

            <date-input
              v-model="staffMemberEmployeeStartDate"
              :label="$t('staff.modals.editStaffMember.inputClientEmployeeStartDateLabel')"
              :disable="readOnly || !hasAccessToStaffMember"
              outlined
              :options="validEmployeeStartDate"
              class="col-12 col-sm-6"
              clearable
            />
            <date-input
              v-model="staffMemberEmployeeEndDate"
              :label="$t('staff.modals.editStaffMember.inputClientEmployeeEndDateLabel')"
              outlined
              :options="validEmployeeEndDate"
              class="col-12 col-sm-6"
              clearable
              :disable="!staffMemberEmployeeStartDate || readOnly || !hasAccessToStaffMember"
            />

            <q-input
              v-model="editStaffMember.notes"
              class="col-12"
              type="textarea"
              autogrow
              :label="$t('staff.modals.editStaffMember.inputClientNotesLabel')"
              :disable="readOnly || !hasAccessToStaffMember"
              outlined
            />

            <template v-if="hasStaffMemberDataFields">
              <template
                v-for="dataField in staffMemberDataFieldsWithoutSection"
                :key="dataField.id"
              >
                <custom-data-field
                  :model-value="getStaffMemberData(dataField)"
                  :data-field="dataField"
                  :client-id="editStaffMember.id"
                  :disable="readOnly || !hasAccessToStaffMember"
                  @update:model-value="setStaffMemberData(dataField.id, $event)"
                />
              </template>

              <div
                v-if="visibleStaffMemberDataFieldsSections.length"
                class="full-width"
              >
                <q-list
                  bordered
                  class="rounded-borders"
                >
                  <q-expansion-item
                    v-for="section in visibleStaffMemberDataFieldsSections"
                    :key="section.id"
                    expand-separator
                    :label="section.label"
                    @show="onStaffMemberDataSectionShown(section)"
                  >
                    <q-card>
                      <q-card-section>
                        <div
                          v-for="dataField in staffMemberDataFields.filter(f => f.section === section?.id)"
                          :key="dataField.id"
                          class="q-mb-md"
                        >
                          <custom-data-field
                            :model-value="getStaffMemberData(dataField)"
                            :data-field="dataField"
                            :client-id="editStaffMember.id"
                            :disable="readOnly || !hasAccessToStaffMember"
                            @update:model-value="setStaffMemberData(dataField.id, $event)"
                          />
                        </div>
                      </q-card-section>
                    </q-card>
                  </q-expansion-item>
                </q-list>
              </div>
            </template>

            <div
              v-if="isUserActive || isCreatingNewUser"
              class="col-12"
            >
              <universal-tooltip
                :show-tooltip="isAddStaffDisabled"
                :text="addStaffTooltipText"
              >
                <q-btn-toggle
                  v-model="editStaffMember.status"
                  :options="staffMemberStatuses"
                  :disable="readOnly || !hasAccessToStaffMember || isAddStaffDisabled"
                  spread
                />
              </universal-tooltip>
            </div>

            <div
              v-if="isLicenseFree"
              class="col-12"
            >
              <banner
                :message="$t('staff.modals.editStaffMember.licenseFreeUntil', {date: licenseFreeUntilFormatted})"
              />
            </div>

            <div
              v-if="isSubscriptionUnavailable && editStaffMember.status === 'active'"
              class="col-12"
            >
              <banner
                type="error"
                :message="$t('clients.modals.editClient.noSubscription')"
              />
            </div>

            <div
              v-else-if="licenseNeeded"
              class="col-12"
            >
              <banner
                :message="$t('staff.modals.editStaffMember.licenseNeededCount', {
                  amount: licenseNeededCountAmount,
                  max: staffSubscription.quantity || 0,
                })"
              />
            </div>
            <div
              v-else-if="showPremiumDowngradeWarning"
              class="col-12"
            >
              <banner
                type="warning"
                :message="$t('staff.modals.editStaffMember.licenseDowngradeWarning')"
              />
            </div>
          </template> <!-- End Staff only information -->
        </div>
      </hr-tab-panel>
      <hr-tab-panel
        name="access"
        class="q-px-none"
      >
        <q-select
          v-show="editUser.id !== userDetails.userId"
          ref="role"
          v-model="editUserRole"
          :options="userRoleList"
          :label="$t('users.modals.editUser.inputRoleLabel')"
          :rules="roleRules"
          :disable="readOnly"
          outlined
          hide-bottom-space
          class="col-12 q-mb-md"
        />

        <banner
          v-if="userCannotHaveClients"
          show-icon
          class="q-mb-md"
          :message="$t('users.modals.editUser.managerClientsInfo', {
            role: $t(`common.userRoles.${editUser.role}`)
          })"
        />

        <!-- The teams this person works in, which route tasks to them. A team never grants access. -->
        <div
          v-if="showTeams"
          class="col-12 q-mb-md"
          data-e2e="field-user-teams"
        >
          <p class="hr-label-large q-mb-xs">
            {{ $t('users.modals.editUser.labelTeams') }}
          </p>
          <hr-option-group
            v-model="editTeams"
            type="checkbox"
            :options="teamOptions"
            :disable="readOnly || !canEditTeams || !userTeams?.loaded.value"
            inline
          />
          <hr-warning-banner
            v-if="teamsHiddenByRole"
            class="q-mt-sm"
            :message="teamsHiddenByRole"
          />
        </div>

        <div
          v-if="connectedClinic.integration && !hideIntegrationControl"
          class="col-12 q-mb-md"
        >
          <q-btn
            v-if="isIntegrationLinked"
            icon="link"
            color="primary"
            outline
            :label="$t('users.modals.editUser.buttonEditIntegration', {
              integration: getIntegrationName(connectedClinic.integration)
            })"
            :disable="readOnly"
            class="full-width"
            @click="onEditIntegration"
          />
          <q-btn
            v-else
            icon="link"
            color="primary"
            outline
            :label="$t('users.modals.editUser.buttonLinkIntegration', {
              integration: getIntegrationName(connectedClinic.integration)
            })"
            :disable="readOnly"
            class="full-width"
            @click="onEditIntegration"
          />
        </div>

        <q-table
          v-if="!userCannotHaveClients"
          v-model:selected="editUser.units"
          flat
          bordered
          :columns="unitsColumns"
          :pagination="unitsPagination"
          hide-bottom
          :rows="filteredUnitsWithClients"
          selection="multiple"
        >
          <template #top>
            <div class="full-width">
              <div class="row items-center">
                <template v-if="!isSearching">
                  <div class="row col items-center q-col-gutter-xs">
                    <filter-chips-bar
                      :filters="filters"
                      :filter-criteria="userClientsFilterCriteria"
                      :filter-groups="userClientsFilterGroups"
                      @update:filters="filters = $event"
                    />
                  </div>
                </template>
                <div
                  :class="{
                    'col': isSearching,
                    'col-auto': !isSearching,
                    'q-pl-sm': !isSearching,
                  }"
                >
                  <search-input
                    ref="searchInput"
                    v-model="clientsSearch"
                    input-class="col"
                    @show-input="onShowSearch"
                    @blur-input="onHideSearch"
                    @hide-input="onHideSearch"
                  />
                </div>
              </div>
            </div>
          </template>
          <template #header-selection>
            <!-- hide whole table selection checkbox -->
          </template>
          <template #header="props">
            <q-tr>
              <!-- empty th for selection column -->
              <q-th />
              <q-th
                v-for="col in props.cols"
                :key="col.name"
                :props="props"
              >
                {{ col.label }}
              </q-th>
              <!-- empty th for expand column -->
              <q-th />
            </q-tr>
          </template>
          <template #body="props">
            <q-tr :props="props">
              <q-td auto-width>
                <q-checkbox
                  v-if="userBindsToUnits && (props.row.id !== '[unassigned]')"
                  :model-value="getUnitChecked(props.row.id)"
                  :val="props.row.id"
                  :disable="readOnly"
                  @update:model-value="onUnitChecked(props.row, $event)"
                />
              </q-td>
              <q-td
                v-for="col in props.cols"
                :key="col.name"
                :props="props"
                class="cursor-pointer text-bold"
                @click="props.expand = !props.expand"
              >
                {{ col.value }}
              </q-td>
              <q-td
                class="text-right cursor-pointer"
                @click="props.expand = !props.expand"
              >
                <q-btn
                  flat
                  :icon="props.expand ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                  @click.stop="props.expand = !props.expand"
                />
              </q-td>
            </q-tr>
            <q-tr
              v-show="props.expand"
              :props="props"
            >
              <q-td colspan="100%">
                <div class="text-left">
                  <div
                    v-if="props.row.clients.length === 0"
                    class="q-ml-lg"
                  >
                    {{ $t('users.modals.editUser.clientTableNoResults') }}
                  </div>
                  <template v-else>
                    <div
                      v-for="unitClient in getClients(props.row.clients)"
                      :key="unitClient.id"
                      class="q-ml-lg row items-center"
                    >
                      <q-checkbox
                        v-model="selectedClients"
                        :val="unitClient"
                        :disable="isClientSelectedByUnit(unitClient.id) || readOnly"
                      >
                        {{ unitClient.clientName }} <span v-if="unitClient.alias">({{ unitClient.alias }})</span>
                        <q-tooltip v-if="isClientSelectedByUnit(unitClient.id)">
                          {{ $t('users.modals.editUser.clientAvailableThroughUnit') }}
                        </q-tooltip>
                      </q-checkbox>
                      <q-chip
                        :label="getClientStatusLabel(unitClient.status)"
                        :color="getClientStatusColor(unitClient.status)"
                        size="sm"
                        text-color="white"
                      />
                    </div>
                    <div
                      v-if="getActiveStaffMembers(props.row.clients).length > 0"
                      class="q-ml-xl q-mt-md text-dark text-bold text-caption"
                    >
                      {{ $t('users.modals.editUser.staffMembers') }}
                    </div>
                    <div
                      v-for="unitStaffMember in getActiveStaffMembers(props.row.clients)"
                      :key="unitStaffMember.id"
                      class="q-ml-lg"
                    >
                      <q-checkbox
                        v-model="selectedClients"
                        :val="unitStaffMember"
                        :disable="isClientSelectedByUnit(unitStaffMember.id) || readOnly"
                      >
                        {{ unitStaffMember.clientName }} <span v-if="unitStaffMember.alias">
                          ({{ unitStaffMember.alias }})
                        </span>
                        <q-tooltip v-if="isClientSelectedByUnit(unitStaffMember.id)">
                          {{ $t('users.modals.editUser.clientAvailableThroughUnit') }}
                        </q-tooltip>
                      </q-checkbox>
                      <q-chip
                        :label="getStaffStatusLabel(unitStaffMember.status)"
                        :color="getClientStatusColor(unitStaffMember.status)"
                        size="sm"
                        text-color="white"
                      />
                    </div>
                  </template>
                </div>
              </q-td>
            </q-tr>
          </template>
        </q-table>
        <div
          class="col-12 q-col-gutter-y-md"
        >
          <div
            v-if="showRequireMFA"
            class="row q-col-gutter-md"
          >
            <universal-tooltip
              show-tooltip
              :text="mfaTooltipText"
            >
              <q-toggle
                v-if="showRequireMFA"
                :model-value="editUser.requireMFA"
                class="q-pl-xs"
                :disable="canRequireMfa || readOnly"
                :label="$t('users.modals.editUser.inputRequireMFALabel')"
                dense
                data-e2e="toggle-require-mfa"
                @update:model-value="onToggleRequireMfa"
              />
            </universal-tooltip>
          </div>

          <div
            class="q-col-gutter-md"
          >
            <div>
              <universal-tooltip
                :text="requireLoginMethodTooltip"
                :delay-time="5000"
                self="center right"
                show-tooltip
                max-width="350px"
              >
                <q-toggle
                  v-model="editUser.requireLoginMethod"
                  class="q-pl-xs"
                  :label="requireLoginMethodLabel"
                  :disable="readOnly"
                  dense
                  data-e2e="toggle-require-login-method"
                />
              </universal-tooltip>
              <login-method-selector
                v-if="editUser.requireLoginMethod"
                v-model="editUser.loginMethod"
                class="q-ml-xs q-mt-sm"
                dense
                show-icon
                data-e2e="selector-login-method"
                :disabled-methods="disabledLoginMethods"
                :disabled-methods-tooltip="$t('users.modals.editUser.tooltipDisableMFAToUseLoginMethod')"
                :disable="readOnly"
              />
            </div>
          </div>
          <div
            v-if="invitePageInput && isDevOrStaging"
            class="col-12 q-col-gutter-y-md"
          >
            <q-toggle
              v-model="editUser.stagingCreateHubspotContact"
              class="q-pl-xs"
              label="Create hubspot contact for staging user"
              dense
            />
          </div>
          <q-card-section
            v-if="(invitePageInput || showRequireMFA) && showMFABanners"
            class="q-px-none"
          >
            <universal-tooltip
              :text="$t('users.modals.editUser.secureAccountWithMFATooltip')"
              show-tooltip
            >
              <banner
                v-if="allowMFA && !editUser.enrolledMFA"
                type="warning"
                :message="$t('users.modals.editUser.secureAccountWithMFA')"
                class="fit-content-flex"
              />
            </universal-tooltip>
            <banner
              v-if="editUser.enrolledMFA"
              type="success"
              :message="$t('users.modals.editUser.mfaEnabled')"
              class="fit-content-flex"
            >
              <template #action>
                <q-btn
                  v-if="isManager && editUser.enrolledMFA"
                  class="q-mr-auto"
                  flat
                  :label="$t('users.modals.editUser.buttonRemoveMFA')"
                  :disable="readOnly"
                  @click="verifyIdentity"
                />
              </template>
            </banner>
          </q-card-section>
        </div>
      </hr-tab-panel>

      <hr-tab-panel
        v-if="canEditAvailability"
        name="availability"
      >
        <availability-editor
          ref="availabilityEditor"
          v-model="editUserAvailability"
        />
      </hr-tab-panel>

      <hr-tab-panel
        name="credentials"
        class="q-px-none"
      >
        <div class="row q-col-gutter-md">
          <div class="col-12">
            <hr-list>
              <hr-list-section-header>
                {{ $t('staff.modals.editStaffMember.titleSupervisors') }}
              </hr-list-section-header>

              <hr-list-item
                v-for="supervisor in filteredStaffMemberSupervisors"
                :key="supervisor.user"
                segmented
                :disable="readOnly || isStandardLicense"
                :label="supervisor.userName"
                data-e2e="list-item-staff-supervisor"
              >
                <template #trailing>
                  <hr-overflow-menu-btn
                    :options="supervisorMenuOptions()"
                    :disable="readOnly || isStandardLicense"
                    @select="onDeleteSupervisor(supervisor)"
                  />
                </template>
              </hr-list-item>

              <!-- The tooltip stays on the add row rather than moving to the list: a standard
                   license is why the action is unavailable, and that is what somebody reaching for
                   it needs told. -->
              <universal-tooltip
                :text="$t('users.modals.editUser.userHasStandardLicense')"
                :show-tooltip="isStandardLicense"
              >
                <hr-list-add-btn
                  :variant="filteredStaffMemberSupervisors.length === 0 ? 'outlined' : 'flat'"
                  :label="$t('staff.modals.editStaffMember.actionAddSupervisor')"
                  :disable="readOnly || isStandardLicense"
                  data-e2e="btn-staff-add-supervisor"
                  @click="onAddSupervisor"
                />
              </universal-tooltip>
            </hr-list>
          </div>
          <div class="col-12">
            <!-- Degrees and certifications, owned by the Staff Competency context. Like the
                       provider panel on the billing tab, it writes nothing: it hands back a patch
                       this editor merges into the draft it is already saving. -->
            <!-- Narrowed by ROLE, like the billing panel on the next tab. `canUpdateClient` admits
                 a therapist holding a grant for this record, but the rules refuse a qualification
                 write from one — and the refusal lands on the whole staff save, so their edits on
                 every other tab would be lost with it. -->
            <qualifications-panel
              :person="editStaffMember"
              :readonly="readOnly || !canEditQualifications"
              @change="onQualificationsChange"
            />
          </div>
          <div class="col-12">
            <hr-list>
              <hr-list-section-header>
                {{ $t('staff.modals.editStaffMember.titleTaskLists') }}
              </hr-list-section-header>

              <hr-list-item
                v-for="taskList in taskListRows"
                :key="taskList.id"
                segmented
                clickable
                :disable="readOnly"
                :label="taskList.label"
                data-e2e="list-item-staff-task-list"
                @click="onEditTaskList(taskList.row)"
              >
                <template #trailing>
                  <hr-overflow-menu-btn
                    :options="taskListMenuOptions()"
                    :disable="readOnly"
                    @click.stop
                    @select="onDeleteTaskList(taskList.row)"
                  />
                </template>
              </hr-list-item>

              <hr-list-add-btn
                :variant="taskListRows.length === 0 ? 'outlined' : 'flat'"
                :label="$t('staff.modals.editStaffMember.actionAddTaskList')"
                :disable="readOnly"
                data-e2e="btn-staff-add-task-list"
                @click="onAddTaskList"
              />
            </hr-list>
          </div>
        </div>
      </hr-tab-panel>
      <hr-tab-panel
        v-if="canEditProviderBilling"
        name="billing"
        class="q-px-none"
      >
        <provider-panel
          :person="editStaffMember"
          :readonly="readOnly || !isManagerOrAdministrator"
          @change="onProviderChange"
        />
      </hr-tab-panel>
    </hr-tab-panels>

    <!-- Only the support-info copy is kept, as on the client editor: it carries the ids support
         actually asks for, where the bare user id was one of them on its own. -->
    <template #destructive-actions>
      <q-field
        outlined
        dense
        readonly
      >
        <template #control>
          <div
            class="self-center full-width no-outline cursor-pointer text-grey-6"
            @click="onCopySupportInfo"
          >
            {{ $t('common.copySupportInfo') }}
          </div>
        </template>
        <template #append>
          <q-icon
            name="content_copy"
            class="cursor-pointer q-ml-xs text-grey-6"
            @click="onCopySupportInfo"
          />
        </template>
        <q-tooltip>{{ $t('common.copySupportInfo') }}</q-tooltip>
      </q-field>
    </template>

    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        :disable="isSavingUser"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        :label="saveButtonLabel"
        :loading="isSavingUser"
        :disable="!canSubmitForm || readOnly"
        data-e2e="button-invite-user-dialog"
      />
    </template>
  </hr-form-dialog>
</template>

<script>
import { callFirebaseFunction, storage } from 'boot/firebase';
import { filterPropertyTypes } from 'boot/globals';
import EditStaffTaskList from 'components/Clients/Modals/EditStaffTaskList';
import EditSupervisor from 'components/Clients/Modals/EditSupervisor';
import DateInput from 'components/DateInput';
import EditIntegration from 'components/Integrations/EditIntegration';
import AvailabilityEditor from 'components/UI/AvailabilityEditor';
import Banner from 'components/UI/Banner';
import CountrySelect from 'components/UI/CountrySelect';
import LoginMethodSelector from 'components/UI/LoginMethodSelector.vue';
import SearchInput from 'components/UI/SearchInput';
import FilterChipsBar from 'components/UI/FilterChipsBar.vue';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import ReAuthenticate from 'components/Users/Modals/ReAuthenticate';
import { Timestamp } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import {
  chain, cloneDeep, difference, every,
  filter, find, get, head, includes, isArray,
  isEmpty, isEqual, map, reject, set, size, trim,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import CustomDataField from 'src/components/DataFields/CustomDataField.vue';
import { useAuth } from 'src/composables/auth';
import { useClients } from 'src/composables/clients';
import { useTime } from 'src/composables/time';
import { SSO_PROVIDER, SSO_PROVIDERS } from 'src/constants/authorization';
import { V2_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import TaskListsDB from 'src/fb/taskLists-db';
import UserPrivilegesDB from 'src/fb/userPrivileges-db';
import UsersDB from 'src/fb/users-db';
import UsersClientsDB from 'src/fb/usersClients-db';
import Auth from 'src/mixins/Auth';
import BusinessUnits from 'src/mixins/BusinessUnitsMixin';
import ClientsMixin from 'src/mixins/ClientsMixin';
import Countries from 'src/mixins/Countries';
import Credentialing from 'src/mixins/Credentialing';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import LicenseProducts from 'src/mixins/LicenseProducts';
import Toolbox from 'src/mixins/Toolbox';
import { editUserProfileAnalytics } from 'src/services/analytics';
import { auth } from 'src/store/auth';
import { clients as clientsStore } from 'src/store/clients';
import { dataFields } from 'src/store/dataFields';
import { firebaseUploader } from 'src/store/firebaseUploader';
import { units as unitsStore } from 'src/store/units';
import {
  copySupportInfoToClipboard,
  getClientInitials,
  isValidEmail,
  userRoles,
} from 'src/utils/AppUtils';
import { calculateAge } from 'src/utils/DateUtils';
import { sendEventToAloha, updateAppointmentUserReference } from 'src/utils/Integrations';
import { logEvent } from 'src/utils/LogEvent';
import { EDIT_USER_PROFILE_SOURCES } from 'src/constants/analytics';
import { useUserTeams } from 'src/composables/userTeams';
import { BILLING_PRIVILEGE } from 'src/constants/privileges';
import { TEAM_IDS } from 'src/contexts/identityAndTenancy';
import { HOLD_TASK_TEAMS, ProviderPanel } from 'src/contexts/revenueCycle';
import { QualificationsPanel } from 'src/contexts/staffCompetency';
import {
  confirmDestructiveDialog,
  HrConfirmBtn, HrDismissBtn, HrFormDialog, HrList, HrListAddBtn, HrListItem, HrListSectionHeader,
  HrOptionGroup, HrOverflowMenuBtn, HrTab, HrTabPanel, HrTabPanels, HrTabs, HrWarningBanner,
} from 'src/design-system';
import { markRaw } from 'vue';
import { EDIT_USER_PROFILE_TABS } from 'src/constants/users';

export default {
  name: 'EditUser',

  components: {
    FilterChipsBar,
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrList,
    HrListAddBtn,
    HrListItem,
    HrListSectionHeader,
    HrOptionGroup,
    HrOverflowMenuBtn,
    HrTab,
    HrTabPanel,
    HrTabPanels,
    HrTabs,
    HrWarningBanner,
    ProviderPanel,
    QualificationsPanel,
    AvailabilityEditor,
    LoginMethodSelector,
    CountrySelect,
    DateInput,
    SearchInput,
    UniversalTooltip,
    CustomDataField,
    Banner,
  },

  mixins: [
    Auth,
    BusinessUnits,
    ClientsMixin,
    Countries,
    Credentialing,
    FiltersMixin,
    Integrations,
    Interactions,
    LicenseProducts,
    Toolbox,
  ],

  props: {
    user: {
      type: Object,
      required: true,
    },

    associatedClients: {
      type: Array,
      required: true,
    },

    isLastActiveClinicManager: {
      type: Boolean,
      default: false,
    },

    returnUnitClients: {
      type: Boolean,
      default: true,
    },

    allUsers: {
      type: Array,
      required: true,
    },

    disableEmail: {
      type: Boolean,
      default: false,
    },

    invitePageInput: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: null,
    },

    saveButton: {
      type: String,
      default: null,
    },

    hideIntegrationControl: {
      type: Boolean,
      default: false,
    },

    /* Manages control over staffProfile creation. By default staffProfile will be created onSubmit, but in case if we
    create new invitation we should perform additional validation on a parent level and create stuffProfile there */
    shouldSaveStaffMember: {
      type: Boolean,
      default: true,
    },

    readOnly: {
      type: Boolean,
      default: false,
    },

    readOnlyMessage: {
      type: String,
      default: null,
    },

    isEditInvitation: {
      type: Boolean,
      default: false,
    },

    initialTab: {
      type: String,
      default: EDIT_USER_PROFILE_TABS.PROFILE,
      validator: (value) => [
        EDIT_USER_PROFILE_TABS.PROFILE,
        EDIT_USER_PROFILE_TABS.ACCESS,
        EDIT_USER_PROFILE_TABS.CREDENTIALS,
        EDIT_USER_PROFILE_TABS.BILLING,
      ].includes(value),
    },

    // Entry point for analytics tagging (e.g. 'scheduling', 'settings').
    source: {
      type: String,
      default: EDIT_USER_PROFILE_SOURCES.UNKNOWN,
      validator: (value) => Object.values(EDIT_USER_PROFILE_SOURCES).includes(value),
    },

  },

  emits: ['hide', 'ok'],

  setup() {
    const {
      createNewStaffMember,
      getUserClientAssociations,
      saveStaffPhoto,
      saveTaskLists,
      getClientProfileImagePath,
      getRandomColor,
    } = useClients();
    const { loginMethodsNames } = useAuth();
    const { getLocaleDateString, addToDate } = useTime();

    return {
      createNewStaffMember,
      getClientInitials,
      getClientProfileImagePath,
      getRandomColor,
      getUserClientAssociations,
      loginMethodsNames,
      saveStaffPhoto,
      saveTaskLists,
      getLocaleDateString,
      addToDate,
    };
  },

  data() {
    return {
      isLoadingStaffMembers: false,
      isLoadingTaskLists: true,
      isSavingUser: false,

      cachedUserPrivileges: {},
      // Raw: a composable's refs, which a reactive data field would unwrap out from under it.
      userTeams: null,

      tab: this.initialTab,
      // Set before validateForm() redirects tab, so the watcher can skip tracking it.
      isTabChangeFromValidation: false,

      isSearching: false,
      clientsSearch: '',
      filters: {},

      userClients: [],
      expandedUnits: [],

      staffMemberSupervisors: [],
      taskLists: [],

      hasAccessToStaffMember: true,
      staffMemberAccessOwnProfile: true,
      isAdminWithMFA: false,

      editUser: {
        userId: null,
        name: null,
        email: null,
        clinic: null,
        role: null,
        requireMFA: false,
        requireLoginMethod: false,
        loginMethod: null,
        stagingCreateHubspotContact: false,
      },

      unitsColumns: [
        {
          name: 'unitName',
          required: true,
          label: this.$t('settings.units.columnUnitLabel'),
          align: 'left',
          field: 'unitName',
          sortable: true,
          sort: (a, b, aObj, bObj) => {
            if (aObj.id === '[unassigned]') {
              return 1;
            }
            if (bObj.id === '[unassigned]') {
              return -1;
            }

            const aUnitName = aObj.unitName?.toString() || '';
            const bUnitName = bObj.unitName?.toString() || '';

            return aUnitName.localeCompare(bUnitName, undefined, { numeric: true, sensitivity: 'base' });
          },
        },
      ],

      unitsPagination: {
        rowsPerPage: 0,
        sortBy: 'unitName',
      },

      selectedUnits: [],
      bottomRowExpanded: false, // table props.expanded doesn't seem to work properly in #bottom-row slot

      editStaffMember: {},
      hasExistingActiveStaffMember: false,

      selectedStaffPhoto: null,
      deleteStaffMemberPhoto: false,

      genderList: map(this.$tm('clients.modals.editClient.gender'), (label, value) => ({ value, label })),

      isLoading: false,

      clientsSyncPromise: null,
      showPremiumDowngradeWarning: false,
    };
  },

  computed: {
    /*
     * Derived once per column change rather than per render: called from the template it
     * returned a fresh array each time, so the chips bar saw a changed prop on every
     * re-render of the parent.
     */
    userClientsFilterCriteria() {
      return this.getFilterCriteriaFromColumns(this.userClientsColumns);
    }, // userClientsFilterCriteria()

    userClientsFilterGroups() {
      return [
        {
          group: 'clientData',
          label: this.$t('history.tableGroups.clientData'),
        },
        {
          group: 'integration',
          label: this.$t('history.tableGroups.integration', {
            integration: this.getIntegrationName(get(this.connectedClinic, 'integration')),
          }),
        },
      ];
    }, // userClientsFilterGroups()

    ...mapState(auth, ['userDetails', 'connectedClinic', 'billing', 'provider',
      'userPrivilegesList',
      'isManagerOrAdministrator',
      'isSupervisor',
      'isManager',
      'isSchedulingAvailable',
      'isRevenueCycleAvailable',
      'isSubscriptionPremium',
      'isSubscriptionUnavailable',
      'getAvailableLicenses',
      'isSubscriptionPastDue',
    ]),
    ...mapState(unitsStore, ['units', 'unitsList']),
    ...mapState(clientsStore, ['clients']),
    ...mapState(dataFields, [
      'staffMemberDataFields',
      'staffMemberSystemDataFields',
      'staffMemberDataFieldSections',
      'staffSchedulingGroupsSection',
    ]),

    canEditAvailability() {
      return this.isSchedulingAvailable
        && (this.isManagerOrAdministrator || this.editUser.id === this.userDetails.userId);
    }, // canEditAvailability()

    /**
     * Whether the billing tab is offered at all.
     *
     * The billing add-on gate rather than the shared-config one: nothing outside billing reads an
     * NPI, or the payer enrollments that will join it here, so a scheduling-only clinic has no use
     * for the tab. There has to be a staff record to hang it on, which there is not while a user is
     * being invited but has no profile yet.
     *
     * Seeing it is not editing it. Whether the fields accept input is the panel's `readonly`, which
     * narrows further to manager and administrator, matching every other billing configuration.
     */
    canEditProviderBilling() {
      return this.isRevenueCycleAvailable && !!this.editStaffMember?.id;
    }, // canEditProviderBilling()

    editUserAvailability: {
      get() {
        return this.editUser?.availability ?? null;
      },
      set(value) {
        this.editUser.availability = value;
      },
    },

    isDevOrStaging() {
      return ['development', 'staging'].includes(process.env.ENV_TYPE);
    }, // isDevOrStaging()

    hasStaffMemberDataFields() {
      return !isEmpty(this.staffMemberDataFields);
    },

    hasStaffMemberSystemDataFields() {
      return !isEmpty(this.staffMemberSystemDataFields);
    },

    canAddPremiumStaffMember() {
      return this.userHasPrivilege('users/addStaffProfiles');
    }, // canAddPremiumStaffMember()

    canRequireMfa() {
      return (this.hasMfaWithoutPremium && !this.editUser.requireMFA) || this.isAdminWithMFA;
    }, // canRequireMfa()

    mfaTooltipText() {
      return this.isAdminWithMFA
        ? this.$t('users.modals.editUser.inputRequireMFAForAdminTooltip')
        : this.$t('users.modals.editUser.inputRequireMFATooltip');
    }, // mfaTooltipText

    hasActiveStaffMember() {
      return get(this.editStaffMember, 'status') === 'active';
    },

    hasUnits() {
      return !isEmpty(this.units);
    }, // hasUnits()

    unitsHint() {
      if (this.userBindsToUnits && !isEmpty(this.editUser.units) && !this.userCannotHaveClients) {
        return this.$t('users.modals.editUser.hintUnitsBinded');
      }
      return undefined;
    }, // unitsHint()

    unitsRules() {
      // Supervisors must assign users to units they are assigned to and cannot assign users to no unit unless
      // they are not assigned to a unit themselves
      if (
        this.hasUnits
        && !this.isManagerOrAdministrator
        && !chain(this.userDetails).get('units').isEmpty().value()
      ) {
        return [(val) => !chain(this.userDetails)
          .get('units', [])
          .intersection(val)
          .isEmpty()
          .value() || this.$t('users.modals.editUser.inputUnitsValidator')];
      }
      return [];
    }, // unitsRules()

    availableUnitsList() {
      return this.unitsList.filter((unit) => this.editUser.units?.includes(unit.value));
    }, // availableUnitsList()

    userRoleList() {
      const userRoleLevel = chain(this.userPrivilegesList)
        .find({ value: this.userDetails.userPrivilege || this.userDetails.role })
        .get('level', 1000)
        .value();

      return this.userPrivilegesList.reduce((acc, userPrivileges) => {
        acc.push({
          ...userPrivileges,
          disable: userPrivileges.level < userRoleLevel,
        });

        return acc;
      }, []);
    }, // userRoleList()

    hasExistingStaffMember() {
      return get(this.user, 'staffMember', null) !== null;
    }, // hasExistingStaffMember()

    isIntegrationLinked() {
      return get(this.editUser, 'integrationReference', null) !== null;
    }, // isIntegrationLinked()

    userClientsColumns() {
      const typeColumns = [
        {
          name: 'type',
          required: true,
          label: this.$t('users.modals.editUser.columnsTypeLabel'),
          align: 'left',
          field: 'type',
          type: 'select',
          options: map(this.$tm('users.modals.editUser.clientTypes'), (typeLabel, typeKey) => ({
            value: typeKey,
            label: typeLabel,
          })),
          format: (clientType) => get(this.$tm('users.modals.editUser.clientTypes'), clientType || 'client', ''),
          sortable: true,

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterProperty: 'type',
          filterItems: map(this.$tm('users.modals.editUser.clientTypes'), (typeLabel, typeKey) => ({
            value: typeKey,
            label: typeLabel,
          })),
        },
      ];
      return chain(typeColumns)
        .concat(
          filter(this.clientColumns, (column) => (chain(column).get('profileTypes').includes('client').value())),
        )
        .concat(
          map(
            this.clientDataColumns,
            (clientDataColumn) => ({
              ...clientDataColumn,
              group: 'clientData',
            }),
          ),
        )
        .value();
    }, // userClientsColumns()

    licenseNeededCountAmount() {
      const { quantity = 0, currentUsage = 0 } = this.staffSubscription || {};
      return Math.max(0, quantity - currentUsage);
    }, // licenseNeededCountAmount()

    staffMembers() {
      return chain(this.clients)
        .filter({ type: 'staff' })
        .filter((client) => (
          (client.status === 'active')
          || (this.user.staffMember === client.id)))
        .map((staffMember) => ({ label: staffMember.clientName, value: staffMember.id }))
        .value();
    }, // staffMembers()

    /**
     * The checklists as rows: the name, and nothing else.
     *
     * NO CAPTION. As a table it carried a status and a date, and neither says anything: the status
     * has exactly one possible value, and when somebody added a checklist answers a question nobody
     * asks of it. A caption that is always there and never varies is noise on every row.
     */
    taskListRows() {
      return this.visibleTaskLists.map((row) => ({
        id: row.id ?? `${row.taskList}-new`,
        row,
        label: get(this.allTaskLists, [row.taskList, 'name'], ''),
      }));
    }, // taskListRows()

    filteredStaffMemberSupervisors() {
      // Don't show self as supervisor even though the user self has access to
      // the staff profile
      return chain(this.staffMemberSupervisors)
        .reject({ editStatus: 'delete' })
        .reject({ user: this.editUser.userId })
        .value();
    }, // filteredStaffMemberSupervisors()

    /**
     * The person's name, not "Edit user: name".
     *
     * A dialog headed by a name is already understood as being about that person, and the verb was
     * the half that repeated on every screen. A caller passing its own `title` still wins, which is
     * how the invite flow names itself.
     */
    /**
     * Supervisor and above, checked strictly.
     *
     * The same bar `firestore.rules` holds a qualification write to. A user with no `role` field
     * reads as false rather than as "not one of the refused roles", because the rule asks the same
     * question the same way and a panel that lets somebody edit what the rules will refuse loses
     * their whole save.
     */
    canEditQualifications() {
      return this.isManagerOrAdministrator === true || this.isSupervisor === true;
    }, // canEditQualifications()

    formTitle() {
      return this.title || this.editUser.name || this.$t('users.modals.editUser.textTitleEditFallback');
    }, // formTitle()

    saveButtonLabel() {
      return this.saveButton || this.$t('common.buttonSave');
    }, // saveButtonLabel()

    userCannotHaveClients() {
      const currentPrivilegeRole = this.userRoleList.find((role) => role.value === this.editUser.role);
      return [userRoles.MANAGER, userRoles.ADMINISTRATOR].includes(currentPrivilegeRole?.role);
    }, // userCanHaveClients()

    visibleTaskLists() {
      return reject(this.taskLists, { editStatus: 'delete' });
    }, // visibleTaskLists()

    editUserRole: {
      get() {
        return find(this.userRoleList, { value: this.editUser.userPrivilege || this.editUser.role });
      },
      set(userRoleInfo) {
        this.editUser.role = userRoleInfo.role;
        this.editUser.userPrivilege = userRoleInfo.isCustomRole ? userRoleInfo.value : null;
      },
    }, // editUserRole

    clientsLookup() {
      return new Map(this.clients.map((client) => [client.id, client]));
    },

    selectedClients: {
      get() {
        const seen = new Set();
        return this.userClients
          .filter((userClientId) => {
            if (seen.has(userClientId)) return false;
            seen.add(userClientId);
            return this.clientsLookup.has(userClientId);
          })
          .map((userClientId) => this.clientsLookup.get(userClientId));
      },
      set(clients) {
        this.userClients = map(clients, (client) => client.id);
      },
    }, // selectedClients()

    expandedUnitsWithSearch: {
      get() {
        if (this.isSearching && this.clientsSearch) {
          return chain(this.unitsWithClients)
            .filter((unit) => !isEmpty(unit.clients))
            .map('id')
            .value();
        }
        return this.expandedUnits;
      },
      set(expandedUnits) {
        if (this.isSearching && this.clientsSearch) {
          return;
        }
        this.expandedUnits = expandedUnits;
      },
    }, // expandedUnitsWithSearch

    allowMFA() {
      return this.isSubscriptionPremium;
    }, // allowMFA()

    requireLoginMethodLabel() {
      return this.invitePageInput
        ? this.$t('users.modals.editUser.requireSignUpMethodLabel')
        : this.$t('users.modals.editUser.requireLoginMethodLabel');
    }, // requireLoginMethodLabel()

    requireLoginMethodTooltip() {
      return this.invitePageInput
        ? this.$t('users.modals.editUser.requireLoginAndSignInMethodTooltip')
        : this.$t('users.modals.editUser.requireLoginMethodTooltip');
    }, // requireLoginMethodTooltip()

    shouldDisableProviderLoginMethods() {
      return this.editUser.requireMFA || this.editUser.enrolledMFA;
    }, // shouldDisableProviderLoginMethods()

    disabledLoginMethods() {
      return this.shouldDisableProviderLoginMethods
        ? Object.values(SSO_PROVIDER)
        : [];
    }, // disabledLoginMethods()

    hasMfaWithoutPremium() {
      return this.editUser.enrolledMFA && !this.allowMFA;
    }, // hasMfaWithoutPremium()

    showRequireMFA() {
      return this.allowMFA || this.hasMfaWithoutPremium;
    }, // showRequireMFA()

    showMFABanners() {
      return (this.allowMFA || this.hasMfaWithoutPremium)
        && !this.invitePageInput && this.tab === EDIT_USER_PROFILE_TABS.ACCESS;
    }, // showMFABanners()

    filteredClients() {
      return this.getFilteredElements(this.clients, this.filters);
    }, // filteredClients()

    filteredUnitsWithClients() {
      if (this.isSearching && this.clientsSearch) {
        const searchTerm = chain(this.clientsSearch).trim().toLower().value();
        return chain(this.unitsWithClients)
          .map((unit) => ({
            ...unit,
            clients: filter(
              unit.clients,
              (client) => chain(client)
                .get('clientName')
                .toLower()
                .includes(searchTerm)
                .value()
          || chain(client)
            .get('alias')
            .toLower()
            .includes(searchTerm)
            .value(),
            ),
          }))
          .reject((unit) => isEmpty(unit.clients))
          .value();
      }

      return chain(this.unitsWithClients)
        .map((unit) => ({
          ...unit,
          clients: this.getFilteredElements(unit.clients, this.filters),
        }))
        .reject((unit) => isEmpty(unit.clients))
        .value();
    }, // filteredUnitsWithClients()

    unitsWithClients() {
      return chain(this.units)
        .map((unit) => ({
          ...unit,
          clients: filter(this.clients, (client) => includes(client.units, unit.id)),
        }))
        .concat({
          id: '[unassigned]',
          unitName: this.$t('users.modals.editUser.unassigned'),
          clients: filter(this.clients, (client) => isEmpty(client.units)),
        })
        .value();
    }, // unitsWithClients()

    userBindsToClientsInUnits() {
      return this.roleHasPrivilege(
        this.cachedUserPrivileges,
        this.userPrivilegeId,
        'clients/accessAllClientsInOwnUnits',
      );
    }, // userBindsToClientsInUnits()

    userBindsToStaffMembersInUnits() {
      return this.roleHasPrivilege(
        this.cachedUserPrivileges,
        this.userPrivilegeId,
        'clients/accessAllStaffMembersInOwnUnits',
      );
    }, // userBindsToStaffMembersInUnits()

    userBindsToUnits() {
      return this.userBindsToClientsInUnits || this.userBindsToStaffMembersInUnits;
    }, // userBindsToUnits()

    clientsByUnits() {
      if (!this.userBindsToUnits) {
        return [];
      }
      return chain(this.unitsWithClients)
        .filter(({ id }) => includes(this.editUser.units, id))
        .map((unit) => chain(unit.clients)
          .reject((client) => (!this.userBindsToClientsInUnits && (client.type !== 'staff')))
          .reject((client) => (!this.userBindsToStaffMembersInUnits && (client.type === 'staff')))
          .map('id')
          .value())
        .flatten()
        .value();
    }, // clientsByUnits()

    // Staff profile related:

    staffMemberStatuses() {
      return map(['inactive', 'active'], (status) => (
        {
          value: status,
          label: get(this.$tm('users.modals.editUser.status'), status),
          disabled: !this.canAddPremiumStaffMember,
          'data-e2e': status === 'active' ? 'button-license-premium' : 'button-license-standard',
        }
      ));
    }, // staffMemberStatuses()

    clientPhoto() {
      if (this.selectedStaffPhoto) {
        return URL.createObjectURL(this.selectedStaffPhoto);
      }

      return this.getClientProfileImagePath(this.editStaffMember, true);
    }, // clientPhoto()

    staffMemberFirstName: {
      get() {
        return this.editStaffMember.firstName;
      },
      set(firstName) {
        const fullName = this.getFullName(this.editStaffMember.firstName, this.editStaffMember.lastName);
        const existingClientName = trim(this.editStaffMember.clientName);
        const existingUserName = trim(this.editUser.name);

        if (!existingClientName || isEqual(fullName, existingClientName)) {
          this.editStaffMember.clientName = this.getFullName(firstName, this.editStaffMember.lastName);
        }
        if (!existingUserName || isEqual(fullName, existingUserName)) {
          this.editUser.name = this.getFullName(firstName, this.editStaffMember.lastName);
        }

        this.editStaffMember.firstName = firstName;
      },
    }, // staffMemberFirstName

    staffMemberLastName: {
      get() {
        return this.editStaffMember.lastName;
      },
      set(lastName) {
        const fullName = this.getFullName(this.editStaffMember.firstName, this.editStaffMember.lastName);
        const existingClientName = trim(this.editStaffMember.clientName);
        const existingUserName = trim(this.editUser.name);

        if (!existingClientName || isEqual(fullName, existingClientName)) {
          this.editStaffMember.clientName = this.getFullName(this.editStaffMember.firstName, lastName);
        }
        if (!existingUserName || isEqual(fullName, existingUserName)) {
          this.editUser.name = this.getFullName(this.editStaffMember.firstName, lastName);
        }

        this.editStaffMember.lastName = lastName;
      },
    }, // staffMemberLastName()

    staffMemberAge() {
      return calculateAge(this.staffMemberBirthDate);
    }, // staffMemberAge()

    staffMemberBirthDate: {
      get() {
        return get(this.editStaffMember, 'birthDate', null);
      },
      set(v) {
        // We set birthDate to 12:00 PM to avoid timezone issues
        this.editStaffMember.birthDate = Timestamp.fromDate(this.addToDate(v.toDate(), { hours: 12 }));
      },
    }, // clientBirthDate()

    staffMemberEmployeeStartDate: {
      get() {
        return get(this.editStaffMember, 'employeeStartDate', null);
      },
      set(v) {
        if (!v) {
          this.editStaffMember.employeeEndDate = null;
        }

        // We set employmentStartDate to 12:00 PM to avoid timezone issues
        this.editStaffMember.employeeStartDate = v
          ? Timestamp.fromDate(this.addToDate(v.toDate(), { hours: 12 }))
          : null;
      },
    }, // staffMemberEmployeeStartDate()

    staffMemberEmployeeEndDate: {
      get() {
        return get(this.editStaffMember, 'employeeEndDate', null);
      },
      set(v) {
        // We set employmentEndDate to 12:00 PM to avoid timezone issues
        this.editStaffMember.employeeEndDate = v
          ? Timestamp.fromDate(this.addToDate(v.toDate(), { hours: 12 }))
          : null;
      },
    }, // staffMemberEmployeeEndDate()

    licenseNeeded() {
      if ((get(this.editStaffMember, 'status') !== 'active')
        || (this.hasExistingActiveStaffMember)
        || (this.isLicenseFree)) {
        return false;
      }

      return this.getAvailableLicenses('staff') < 1;
    }, // loadStaffMemberSupervisors

    licenseFreeUntilFormatted() {
      if (!(this.licenseFreeUntil instanceof Timestamp)) {
        return null;
      }
      return this.getLocaleDateString(this.licenseFreeUntil);
    }, // loadStaffMemberSupervisors()

    licenseFreeUntil() {
      return get(this.editStaffMember, 'licenseFreeUntil', null);
    }, // licenseFreeUntil()

    isLicenseFree() {
      return (this.licenseFreeUntil instanceof Timestamp)
        && (this.licenseFreeUntil.toDate() > new Date());
    }, // isLicenseFree()

    selectedStaffMemberUnit: {
      get() {
        return chain(this.editStaffMember).get('units').head().value();
      },
      set(unitId) {
        this.editStaffMember.units = unitId ? [unitId] : [];
      },
    }, // selectedStaffMemberUnit

    userPrivilegeId() {
      return this.editUser.userPrivilege || this.editUser.role;
    }, // userPrivilegeId()

    /**
     * Teams route billing tasks, the only tasks so far; a parent is never on one. Shown only to who
     * can read them, the person and the managers and administrators who set them: anybody else would
     * see the role's default in place of what was saved.
     */
    showTeams() {
      return this.isRevenueCycleAvailable
        && Boolean(this.editUser.id)
        && this.editUser.role !== userRoles.PARENT
        && (this.canEditTeams || this.editUser.id === this.userDetails.userId);
    }, // showTeams()

    canEditTeams() {
      return this.isManagerOrAdministrator;
    }, // canEditTeams()

    editTeams: {
      get() {
        return this.userTeams?.teams.value ?? [];
      },
      set(teams) {
        this.userTeams.teams.value = teams;
      },
    }, // editTeams

    teamOptions() {
      return TEAM_IDS.map((team) => ({ value: team, label: this.$t(`users.teams.${team}`) }));
    }, // teamOptions()

    /**
     * Teams never grant access, so somebody can be on a team whose tasks their role cannot see.
     * Said here, where both are set, rather than discovered as an empty worklist.
     */
    teamsHiddenByRole() {
      if (this.roleHasPrivilege(this.cachedUserPrivileges, this.userPrivilegeId, BILLING_PRIVILEGE)) {
        return null;
      }
      const hidden = this.editTeams.filter((team) => HOLD_TASK_TEAMS.includes(team));

      return hidden.length > 0
        ? this.$t('users.modals.editUser.textTeamsHiddenByRole', {
          teams: hidden.map((team) => this.$t(`users.teams.${team}`)).join(', '),
        })
        : null;
    }, // teamsHiddenByRole()

    staffMemberInfo() {
      return {
        editStaffMember: this.editStaffMember,
        licenseNeeded: this.licenseNeeded,
        taskLists: this.taskLists,
        selectedStaffPhoto: this.selectedStaffPhoto,
        hasStaffMember: true,
        selectedClients: this.selectedClients,
        hasActiveStaffMember: this.hasActiveStaffMember,
        staffMemberAccessOwnProfile: this.staffMemberAccessOwnProfile,
        staffMemberSupervisors: this.staffMemberSupervisors,
        returnUnitClients: this.returnUnitClients,
        clientsByUnits: this.clientsByUnits,
        associatedClients: this.associatedClients,
      };
    }, // staffMemberInfo()

    modalTabs() {
      const tabs = [
        {
          name: EDIT_USER_PROFILE_TABS.PROFILE,
          label: this.$t('clients.modals.editClient.tabProfile'),
        },
        {
          name: EDIT_USER_PROFILE_TABS.ACCESS,
          label: this.$t('users.modals.editUser.tabAccess'),
        },
        {
          name: EDIT_USER_PROFILE_TABS.CREDENTIALS,
          label: this.$t('users.modals.editUser.tabCredentials'),
        },
      ];

      if (this.canEditProviderBilling) {
        tabs.push({
          name: EDIT_USER_PROFILE_TABS.BILLING,
          label: this.$t('users.modals.editUser.tabBilling'),
        });
      }

      if (this.canEditAvailability) {
        tabs.splice(2, 0, {
          name: 'availability',
          label: this.$t('users.modals.editUser.tabAvailability'),
        });
      }

      return tabs;
    }, // modalTabs()

    staffMemberDataFieldsWithoutSection() {
      const availableSections = new Set(this.staffMemberDataFieldSections.map(({ id }) => id));

      return this.staffMemberDataFields.filter((field) => {
        if (!field.section) {
          return true;
        }

        return !availableSections.has(field.section);
      });
    }, // staffMemberDataFieldsWithoutSection()

    visibleStaffMemberDataFieldsSections() {
      return this.staffMemberDataFieldSections.filter((section) => {
        return section.isDefaultSection ? section.isVisible : true;
      });
    }, // visibleStaffMemberDataFieldsSections()

    isRoleOfParent() {
      return this.userRoleList
        .find((role) => role.value === this.editUser.role)?.role === userRoles.PARENT;
    }, // isRoleOfParent()

    showStaffFields() {
      return !this.isRoleOfParent;
    }, // showStaffFields()

    isStandardLicense() {
      return this.editStaffMember.status === 'inactive';
    },

    isPremiumLicense() {
      return this.editStaffMember.status === 'active';
    }, // isPremiumLicense()

    isCreatingNewUser() {
      return !this.user.id;
    }, // isCreatingNewUser()

    showLicenseBanner() {
      // Hide for parent role
      if (this.user.role === userRoles.PARENT || this.isRoleOfParent) {
        return false;
      }

      if (this.isAddStaffDisabled) {
        return false;
      }

      if (!this.isUserActive) {
        return false;
      }

      // Show if no active staff member exists or editing inactive staff
      if (!this.hasExistingActiveStaffMember || this.editStaffMember.status === 'inactive') {
        return true;
      }

      // Otherwise, show only if creating a new user
      return this.isCreatingNewUser;
    }, // showLicenseBanner()

    staffSubscription() {
      return get(this.billing, ['subscriptionLicenses', 'staff'], {});
    }, // staffSubscription()

    canSubmitForm() {
      if (this.isSubscriptionUnavailable && this.editStaffMember.status === 'active') {
        return false;
      }

      return true;
    },

    isUserActive() {
      return this.editUser.status === 'active';
    },

    isExistingUser() {
      return !!this.editUser.userId;
    },

    showDisabledUserBanner() {
      return !this.isUserActive && this.isExistingUser;
    },

    isAddStaffDisabled() {
      return !this.canAddPremiumStaffMember || this.isSubscriptionPastDue;
    },

    addStaffTooltipText() {
      if (!this.canAddPremiumStaffMember) {
        return this.$t('settings.users.disabledAddPremiumLicense');
      }

      if (this.isSubscriptionPastDue) {
        return this.$t('clients.dialogAddStaffUnavailableMessage');
      }

      return '';
    },

    roleRules() {
      return [
        (val) => !!val || this.$t('users.modals.editUser.inputValidatorSelectRole'),
        (val) => {
          if (
            this.user?.role === userRoles.MANAGER
            && this.isLastActiveClinicManager
            && val?.role !== userRoles.MANAGER
          ) {
            return this.$t('users.modals.editUser.inputValidatorClinicManagerRole');
          }

          return true;
        },
      ];
    },
  },

  watch: {
    user: {
      immediate: true,
      deep: true,
      async handler(user) {
        const userPrivilegeId = user.userPrivilege || user.role;
        await this.refreshCachedUserPrivileges(userPrivilegeId);
        const businessUnitsIdsFromOptionsList = this.unitsList.map((unit) => unit.value);
        const validBusinessUnits = this.getBusinessUnitsWithoutDeletedEntries(
          user.units,
          businessUnitsIdsFromOptionsList,
        );
        this.editUser = cloneDeep(user);
        this.editUser.requireMFA = user.requireMFA || false;
        this.editUser.requireLoginMethod = user.requireLoginMethod || false;
        this.editUser.stagingCreateHubspotContact = false;
        this.editUser.loginMethod = user.loginMethod || null;
        this.editUser.units = validBusinessUnits;
      },
    },

    tab(newTab, oldTab) {
      if (this.isTabChangeFromValidation) {
        this.isTabChangeFromValidation = false;
        return;
      }

      editUserProfileAnalytics.trackProfileTabSwitched({
        targetUserId: this.user.id, source: this.source, fromTab: oldTab, toTab: newTab,
      });
    }, // tab

    userPrivilegeId: {
      immediate: true,
      async handler(userPrivilegeId) {
        this.refreshCachedUserPrivileges(userPrivilegeId);
      },
    },

    associatedClients: {
      immediate: true,
      deep: true,
      handler(to) {
        if (!isArray(to)) {
          this.userClients = [];
          return;
        }
        this.userClients = [...to];
      },
    },

    'editUser.units': {
      handler(newUnits) {
        if (this.selectedStaffMemberUnit && !newUnits?.includes(this.selectedStaffMemberUnit)) {
          this.selectedStaffMemberUnit = null;
        }
      },
    },

    clientsByUnits: {
      /*
       todo [vue3-migration]: it would be better to add a deep option, but watcher should be triggered anyway,
         because it is computed property we are watching
      */
      deep: true,
      immediate: true,
      handler(newValue, oldValue) {
        /*
          todo [vue3-migration]: newValue and oldValue would be different arrays because we are watching the
            computed property, so it returns a new array on every calculation.
         */
        // no need to handle anything if it's an initial trigger with empty values
        if (isEmpty(newValue) && isEmpty(oldValue)) {
          return;
        }

        if (size(newValue) > size(oldValue)) {
          this.selectedClients = chain(this.selectedClients)
            .concat(filter(this.clients, (client) => includes(newValue, client.id)))
            .uniqBy('id')
            .value();
        } else {
          const removedClients = difference(oldValue, newValue);
          this.selectedClients = reject(this.selectedClients, ({ id }) => includes(removedClients, id));
        }
      },
    },

    'editStaffMember.status': {
      handler(newValue, oldValue) {
        // get current status of the staff member
        const staffMember = chain(this.clients)
          .find({ id: this.user.staffMember })
          .cloneDeep()
          .value();

        let existingStatus = 'inactive';
        if (staffMember) {
          existingStatus = get(staffMember, 'status', 'inactive');
        }

        // if we are editing an exiting user and changing status to inactive - show warning
        if (this.editUser.id
          && newValue === 'inactive'
          && oldValue === 'active'
          && existingStatus === 'active'
        ) {
          this.showPremiumDowngradeWarning = true;
        } else {
          this.showPremiumDowngradeWarning = false;
        }
      },
    },
  },

  async created() {
    this.userTeams = markRaw(useUserTeams({
      clinic: () => this.userDetails.clinic,
      uid: () => this.editUser.id ?? null,
      role: () => this.editUser.role ?? null,
    }));
    // The user arrives after an async privilege refresh, so the saved teams load once it has: a
    // default shown in their place would be saved over them by the next tick of a checkbox.
    this.$watch(
      () => (this.showTeams ? this.editUser.id : null),
      (uid) => {
        if (uid) {
          this.userTeams.load().catch((error) => console.error('Failed to load a user\'s teams', error));
        }
      },
      { immediate: true },
    );
    await this.initializeClients();
    await this.waitForClientsSync();
    this.expandedUnits = chain(this.unitsWithClients)
      .reject((unit) => includes(this.editUser.units, unit.id))
      .filter((unit) => !chain(unit)
        .get('clients')
        .intersection(this.selectedClients)
        .isEmpty()
        .value())
      .map('id')
      .value();

    this.isAdminWithMFA = await this.getAdminMFAStatus() || false;
  },

  mounted() {
    this.editUser.role = this.userRole;
  },

  methods: {
    isValidEmail,
    ...mapActions(clientsStore, [
      'initializeClients',
      'syncClients',
      'createClient',
      'updateClient',
      'removeLoadedClient',
    ]),
    ...mapActions(firebaseUploader, ['addToUploadQueue']),
    ...mapActions(unitsStore, ['loadClinicUnits']),
    ...mapActions(auth, ['reloadUserDetails']),

    // following method is REQUIRED
    // (don't change its name --> "show")
    async show() {
      this.$refs.dialog.show();
      this.loadClinicUnits();
      await this.waitForClientsSync();
      this.loadStaffMember();
      this.loadStaffMemberSupervisors();
      this.loadTaskLists();
    }, // show()

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    // noticed that `show` might fire before `create` and `this.clients` end up not loaded
    // this can cause issues while saving the user, thus this method waits for it to load
    async waitForClientsSync() {
      if (!this.user.staffMember) {
        return;
      }
      try {
        this.removeLoadedClient(this.user.staffMember); // always re-load staff client
        if (this.clientsSyncPromise === null) {
          this.clientsSyncPromise = this.syncClients();
          this.$q.loading.show();
        }
        await this.clientsSyncPromise;
      } catch (error) {
        console.error(error);
        logEvent(error);
        this.notifyNegative(this.$t('users.modals.editUser.usersSyncError', { error }));
        this.hide();
      } finally {
        this.$q.loading.hide();
      }
    }, // waitForClientsSync()

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    }, // onDialogHide()

    async validateForm() {
      if (!this.editUser.role) {
        this.isTabChangeFromValidation = this.tab !== EDIT_USER_PROFILE_TABS.ACCESS;
        this.tab = EDIT_USER_PROFILE_TABS.ACCESS;
        return false;
      }

      const validateInfo = [
        this.$refs.email && this.$refs.email.validate(),
      ];
      if (this.$refs.name) {
        validateInfo.push(this.$refs.name.validate());
      }
      if (this.$refs.units) {
        validateInfo.push(this.$refs.units.validate());
      }

      const validateStaffProfile = [];
      if (this.$refs.clientName) {
        validateStaffProfile.push(this.$refs.clientName.validate());
      }
      if (this.$refs.country) {
        validateStaffProfile.push(this.$refs.country.validate());
      }
      if (
        !every(await Promise.all(validateStaffProfile))
        || !every(await Promise.all(validateInfo))
      ) {
        this.isTabChangeFromValidation = this.tab !== EDIT_USER_PROFILE_TABS.PROFILE;
        this.tab = EDIT_USER_PROFILE_TABS.PROFILE;

        // Scroll to the first invalid field. The form is the dialog's own now, so the search
        // starts from the dialog's element rather than from a form ref this component used to own.
        this.$nextTick(() => {
          const firstInvalidField = this.$refs.dialog?.$el?.querySelector('.q-field--error')
            ?? document.querySelector('.hr-form-dialog .q-field--error');
          if (firstInvalidField) {
            firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });

        return false;
      }

      // HrFormDialog owns the form and publishes its validation, so this asks the dialog.
      if (!await this.$refs.dialog.validate()) {
        this.notifyNegative(this.$t('common.validateFieldFailed'));
        return false;
      }

      return true;
    }, // validateForm()

    async updateExistingAppointment(userData, oldIntegrationRef, newIntegrationRef) {
      await updateAppointmentUserReference(
        this.connectedClinic.id,
        newIntegrationRef || oldIntegrationRef,
        userData,
      );
      const eventType = newIntegrationRef === null ? 'MemberMappingsRemoved' : 'MemberMappingsAdded';
      await sendEventToAloha(this.connectedClinic.id, eventType, { refId: newIntegrationRef || oldIntegrationRef });
    }, // updateExistingAppointment()

    async updateUserInExistingAppointments() {
      const currentIntegration = this.connectedClinic.integration;

      if (currentIntegration === 'aloha') {
        await this.updateUserInAlohaAppointments();
      }

      if (V2_DRIVEN_INTEGRATIONS.includes(currentIntegration)) {
        const oldRef = get(this.user, 'integrationReference', null);
        const newRef = get(this.editUser, 'integrationReference', null);
        if (newRef !== null && oldRef !== newRef) {
          callFirebaseFunction('backfillV2AppointmentsForRef', {
            clinicId: this.connectedClinic.id,
            refId: newRef,
            objectType: 'staff',
          }).catch((err) => console.error('backfillV2AppointmentsForRef failed', err));
        }
      }

      // check if we updating an existing user and not an invited user.
      if (!this.editUser.userId) {
        return;
      }

      if (this.user.name !== this.editUser.name) {
        await callFirebaseFunction('updateUserNameReferences', {
          clinicId: this.connectedClinic.id,
          userId: this.editUser.userId,
          newUserName: this.editUser.name,
        });
      }
    }, // updateUserInExistingAppointments()

    async updateUserInAlohaAppointments() {
      const oldIntegrationRef = get(this.user, 'integrationReference', null);
      const newIntegrationRef = get(this.editUser, 'integrationReference', null);

      if (this.connectedClinic.integration === 'aloha' && (oldIntegrationRef !== newIntegrationRef)) {
        const userData = {
          userId: newIntegrationRef ? this.editUser.userId : null,
          userName: newIntegrationRef ? this.editUser.name : null,
        };

        try {
          // when reference is switched from one aloha profile to another, we need to update apts for two profiles
          if (oldIntegrationRef && newIntegrationRef) {
            await Promise.all([
              this.updateExistingAppointment(userData, null, newIntegrationRef),
              this.updateExistingAppointment(userData, oldIntegrationRef, null),
            ]);
          } else {
            await this.updateExistingAppointment(userData, oldIntegrationRef, newIntegrationRef);
          }
        } catch (err) {
          console.error(err);
          logEvent(err);
          this.notifyNegative(err.message);
        }
      }
    }, // updateUserInExistingAppointments()

    async onSubmit() {
      if (this.isSavingUser) {
        return;
      }

      this.isSavingUser = true;
      try {
        if (!await this.validateForm() || !this.canSubmitForm) {
          return;
        }

        if (this.$refs.availabilityEditor?.hasErrors) {
          this.notifyNegative(this.$t('availabilityEditor.notifyConflict'));
          return;
        }

        if (this.hasAccessToStaffMember) {
          await this.saveStaffMember();
        }

        if (this.editUser.id) {
          await this.updateUserInExistingAppointments();
        }

        if (this.showTeams && this.canEditTeams && !this.readOnly) {
          await this.userTeams.save();
        }

        const forceUpdate = this.editUser.role !== this.user.role
            || this.editUser.userPrivilege !== this.user.userPrivilege;

        const userClientAssociations = this.getUserClientAssociations(
          this.editUser,
          this.staffMemberInfo,
          forceUpdate,
        );

        if ((!this.invitePageInput && !this.isEditInvitation) || !this.isDevOrStaging) {
          delete this.editUser.stagingCreateHubspotContact;
        }

        this.$emit('ok', {
          user: this.editUser,
          userClientAssociations,
          invitePage: 'signup',
          staffMemberInfo: this.staffMemberInfo,
        });

        this.hide();
      } catch (error) {
        this.notifyNegative(error.message);
      } finally {
        this.isSavingUser = false;
      }
    }, // onSubmit()

    onToggleRequireMfa(value) {
      this.editUser.requireMFA = value;

      if (SSO_PROVIDERS.includes(this.editUser.loginMethod)) {
        this.editUser.loginMethod = null;
      }
    }, // onToggleRequireMfa()

    async verifyIdentity() {
      if (this.provider === SSO_PROVIDER.MICROSOFT) {
        // For now let users remove MFA without being asked for re-authentication if they are using Microsoft provider
        // TODO: implement Microsoft re-authentication functionality
        await this.removeMFA();

        if (this.editUser.userId === this.userDetails.userId) {
          window.location.reload();
        }
        return;
      }

      this.openComponentDialog(
        ReAuthenticate,
        {
          reAuthenticateTitle: this.$t('users.modals.reAuthenticate.verifyIdentity'),
          caption: this.$t('users.modals.reAuthenticate.verifyIdentityRemoveCaption'),
          providerMode: 're-auth',
        },
        async (event) => {
          if (event?.closeParent) {
            this.hide();
            return;
          }

          await this.removeMFA();

          if (this.editUser.userId === this.userDetails.userId) {
            window.location.reload();
          }
        },
      );
    }, // verifyIdentity()

    async removeMFA() {
      this.$q.loading.show();

      try {
        await callFirebaseFunction('removeMFA', {
          userId: this.editUser.userId,
          clinicId: this.userDetails.clinic,
        });

        this.$q.loading.hide();
        this.notifySuccess(this.$t('users.modals.editUser.buttonRemoveMFASuccess'));

        // We are setting enrolledMFA on the backend, so we need to update it here so as not
        // to reload the user and remove the 'removeMFA' button
        this.editUser.enrolledMFA = false;
      } catch (e) {
        this.$q.loading.hide();
        this.notifyNegative(e.message);
      }
    }, // removeMFA()

    onShowSearch() {
      this.isSearching = true;
    }, // onShowSearch()

    onHideSearch() {
      this.isSearching = false;
    }, // onHideSearch()

    onEditIntegration() {
      this.openComponentDialog(
        EditIntegration,
        {
          integrationReference: get(this.editUser, 'integrationReference'),
          savedIntegrationReference: get(this.user, 'integrationReference'),
          objectType: 'staff',
          userId: this.editUser.userId,
        },
        (integrationProfileInfo) => {
          if (integrationProfileInfo === null) {
            this.editUser.integrationReference = null;
          } else {
            this.editUser = {
              ...this.editUser,
              integrationReference: integrationProfileInfo.refId || null,
              name: this.editUser.name || integrationProfileInfo.fullName || '',
            };
          }
        },
      );
    }, // onEditIntegration()

    onUnitChecked(row, checked) {
      if (!chain(this.editUser).get('units').isArray().value()) {
        this.editUser.units = [];
      }
      if (checked) {
        this.editUser.units.push(row.id);
      } else {
        this.editUser.units = reject(this.editUser.units, (unitId) => unitId === row.id);
      }
    }, // onUnitChecked()

    onSelectUploadedFile(evt) {
      const file = head(evt.target.files || evt.dataTransfer.files);

      if (!file) {
        return;
      }

      const extension = chain(file)
        .get('name')
        .split('.')
        .last()
        .value();

      if (!extension) {
        return;
      }

      this.selectedStaffPhoto = file;
      this.deleteStaffMemberPhoto = false;

      if (this.$refs.file) {
        this.$refs.file.value = '';
      }
    }, // onSelectUploadedFile()

    onPhotoDelete() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('staff.modals.editStaffMember.dialogdeleteStaffMemberPhoto'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          this.editStaffMember.photo = null;
          this.selectedStaffPhoto = null;

          if (this.editStaffMember.photo) {
            this.deleteStaffMemberPhoto = true;
          }
        });
    }, // onPhotoDelete()

    /**
     * Merges the billing identity panel's patch into the staff member draft.
     *
     * A MERGE INTO THE DRAFT RATHER THAN A WRITE, which is why the panel emits at all. The NPI lives
     * on the very document this dialog saves, so writing it from the panel would be a second writer
     * against a record being edited and this dialog's own save would put its stale copy back over
     * the top. One writer: the existing save, which already sends the whole draft.
     */
    onProviderChange(patch) {
      Object.assign(this.editStaffMember, patch);
    }, // onProviderChange()

    onQualificationsChange(patch) {
      Object.assign(this.editStaffMember, patch);
    }, // onQualificationsChange()

    // One action, so the menu holds one item. Keeping it in the overflow rather than as a bare icon
    // matches the credential rows beside it, and leaves room for the second action to arrive.
    supervisorMenuOptions() {
      return [{
        group: 'destructive',
        options: [{
          label: this.$t('common.buttonDelete'), icon: 'delete', value: 'remove', destructive: true,
        }],
      }];
    }, // supervisorMenuOptions()

    onAddSupervisor() {
      const excludeUsers = chain(this.staffMemberSupervisors)
        .map('user')
        .concat(this.editUser.userId)
        .value();
      this.openComponentDialog(
        EditSupervisor,
        {
          allUsers: this.allUsers,
          excludeUsers,
          canDelete: false,
        },
        (newSupervisor) => {
          if (!newSupervisor) {
            return;
          }
          this.staffMemberSupervisors = [
            ...this.staffMemberSupervisors,
            {
              editStatus: 'create',
              client: null,
              role: newSupervisor.role,
              user: newSupervisor.userId,
              userName: newSupervisor.name,
            },
          ];
        },
      );
    }, // onAddSupervisor()

    onDeleteSupervisor(staffMemberSupervisor) {
      // The design system's confirmation rather than the raw Quasar dialog, which is the same swap
      // the rest of this tab's content made: one wording and one shape for a destructive step.
      confirmDestructiveDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('common.notifyDeleteConfirm'),
        () => {
          const staffMemberSupervisorIdx = chain(this.staffMemberSupervisors)
            .findIndex((supervisor) => (
              (supervisor.user === staffMemberSupervisor.user)
              && (supervisor.editStatus !== 'delete')))
            .value();
          if (staffMemberSupervisorIdx === -1) {
            return;
          }
          if (this.staffMemberSupervisors[staffMemberSupervisorIdx].editStatus === 'create') {
            this.staffMemberSupervisors = reject(this.staffMemberSupervisors, staffMemberSupervisor);
          } else {
            this.staffMemberSupervisors[staffMemberSupervisorIdx].editStatus = 'delete';
          }
        },
        { confirmLabel: this.$t('common.buttonDelete') },
      );
    }, // onDeleteSupervisor()

    onAddTaskList() {
      this.openComponentDialog(
        EditStaffTaskList,
        {},
        (newTaskList) => {
          this.taskLists = [...this.taskLists, newTaskList];
        },
      );
    }, // onAddTaskList()

    taskListMenuOptions() {
      return [{
        group: 'destructive',
        options: [{
          label: this.$t('common.buttonDelete'), icon: 'delete', value: 'remove', destructive: true,
        }],
      }];
    }, // taskListMenuOptions()

    onEditTaskList(taskList) {
      if (this.readOnly) {
        return;
      }

      this.openComponentDialog(
        EditStaffTaskList,
        { taskList },
        (newTaskList) => {
          // Matched on the row handed over rather than on an index, so nothing depends on the
          // position of a list the dialog cannot see.
          this.taskLists = this.taskLists.map((entry) => (entry === taskList ? newTaskList : entry));
        },
      );
    }, // onEditTaskList()

    /**
     * Removes a checklist, which until now it did not.
     *
     * The delete lived inside the edit dialog and spliced `credentials` — the wrong array entirely,
     * left over from the credential editor it was copied from — and only for a row with no id, so a
     * saved checklist could not be removed at all and an unsaved one took a credential with it.
     *
     * A saved row is MARKED for deletion rather than dropped, because that is what the save path
     * reads: `saveTaskLists` acts on `editStatus`, and a row simply missing from the array is a row
     * it never hears about. An unsaved one has nothing to tell the server, so it just goes.
     */
    onDeleteTaskList(taskList) {
      confirmDestructiveDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('common.notifyDeleteConfirm'),
        () => {
          this.taskLists = taskList.id
            ? this.taskLists.map((entry) => (
              entry === taskList ? { ...entry, editStatus: 'delete' } : entry))
            : reject(this.taskLists, (entry) => entry === taskList);
        },
        { confirmLabel: this.$t('common.buttonDelete') },
      );
    }, // onDeleteTaskList()

    getDefaultStaffMember() {
      const units = this.hasUnits
        ? chain(this.editUser).get('units').take(1).value()
        : [];

      return {
        clinic: this.userDetails?.clinic,
        type: 'staff',
        user: this.user.userId || null,

        clientName: this.editUser.name || '',
        country: this.connectedClinic.country,
        photo: null,
        units,
        status: 'inactive',
        credentials: [],
        avatarBgColor: this.getRandomColor(),
      };
    }, // getDefaultStaffMember()

    async refreshCachedUserPrivileges(userPrivilegeId) {
      if (!userPrivilegeId) {
        return;
      }
      this.isLoading = true;
      const userPrivilegesDb = new UserPrivilegesDB(this.userDetails.clinic);
      const rolePrivileges = await userPrivilegesDb.read(userPrivilegeId);

      set(this.cachedUserPrivileges, userPrivilegeId, rolePrivileges || {});
      this.isLoading = false;
    }, // refreshCachedUserPrivileges()

    loadStaffMember() {
      const staffMember = chain(this.clients)
        .find({ id: this.user.staffMember })
        .cloneDeep()
        .value();

      if (this.user.staffMember && (!staffMember && !this.isManagerOrAdministrator)) {
        this.hasAccessToStaffMember = false;
        return;
      }

      if (!staffMember) {
        this.editStaffMember = this.getDefaultStaffMember();
        return;
      }

      this.editStaffMember = {
        ...staffMember,
        ...(!staffMember.clientName && this.user.name && this.user.name !== this.user.email
          ? { clientName: this.user.name }
          : {}
        ),
      };
      this.hasExistingActiveStaffMember = this.editStaffMember.status === 'active';
    },

    async loadStaffMemberSupervisors() {
      if (get(this.editStaffMember, 'id', null) === null) {
        return;
      }

      this.usersClientsDb = new UsersClientsDB();
      const usersClients = await this.usersClientsDb.readAll([
        ['client', '==', this.editStaffMember.id],
      ]);
      this.staffMemberSupervisors = usersClients;
    }, // loadStaffMemberSupervisors()

    async loadTaskLists() {
      this.isLoadingTaskLists = true;
      if (this.editStaffMember.id) {
        const taskListsDb = new TaskListsDB(this.editStaffMember.id);
        this.taskLists = await taskListsDb.readAll();
      }
      this.isLoadingTaskLists = false;
    }, // loadTaskLists()

    async saveStaffMember() {
      // Make sure we evaluate the licenseNeeded before we save the staff member
      const { licenseNeeded } = this;
      try {
        if (this.editUser.id && !this.editStaffMember.id) {
          // Create a new client (client.id is empty)
          const id = await this.createNewStaffMember(this.staffMemberInfo);
          this.editStaffMember.id = id;
          this.editUser.staffMember = id || null;
          return;
        }

        if (!this.editStaffMember.id) {
          return;
        }

        // Update avatar bg color
        if (this.editStaffMember.avatarBgColor === undefined || this.editStaffMember.avatarBgColor === null) {
          this.editStaffMember.avatarBgColor = this.getRandomColor();
        }

        this.editStaffMember.avatarInitials = this.getClientInitials(
          this.editStaffMember.clientName,
        );

        const promises = [
          this.updateClient(this.editStaffMember),
          this.saveStaffPhoto(this.editStaffMember.id, this.selectedStaffPhoto),
        ];

        if (this.deleteStaffMemberPhoto) {
          const staffMemberPhotoRef = ref(storage, this.editStaffMember.photo);

          if (staffMemberPhotoRef) {
            promises.push(deleteObject(staffMemberPhotoRef));
          }
        }
        // Update an existing client (client.id is empty)
        await Promise.all(promises);

        const initStaff = !this.billing?.subscriptionLicenses?.staff?.price
          && licenseNeeded;
        this.updateLicenseUsage(licenseNeeded, initStaff);
        this.editUser.staffMember = this.editStaffMember.id || null;

        if (this.editStaffMember.id) {
          await this.saveTaskLists(this.editStaffMember.id, this.taskLists);
        }
      } catch (error) {
        logEvent(error);
        this.notifyNegative(error.message);
      }
    }, // saveStaffMember()

    saveUser() {
      const usersDb = new UsersDB();
      return usersDb.update(this.editUser);
    }, // saveUser()

    isClientSelectedByUnit(clientId) {
      return includes(this.clientsByUnits, clientId);
    }, // isClientSelectedByUnit()

    getClients(clients) {
      return reject(clients, { type: 'staff' });
    },

    getActiveStaffMembers(clients) {
      // Don't show inactive staff members in the list
      return clients.filter((client) => (client.type === 'staff' && client.status === 'active'));
    }, // getActiveStaffMembers()

    getClientStatusLabel(status) {
      return get(this.$tm('clients.status'), status);
    }, // getClientStatusLabel()

    getStaffStatusLabel(status) {
      return get(this.$tm('staff.status'), status);
    }, // getStaffStatusLabel()

    getClientStatusColor(status) {
      return (status === 'active') ? 'positive' : 'warning';
    }, // getClientStatusColor()

    maxBirthDate(d) {
      return d <= this.formatDate(new Date(), 'yyyy/MM/dd');
    }, // maxBirthDate()

    validEmployeeStartDate(d) {
      if (this.editStaffMember.employeeEndDate) {
        return d < this.formatDate(this.editStaffMember.employeeEndDate.toDate(), 'yyyy/MM/dd');
      }

      // Allow date to be 1 year in the future
      return d <= this.formatDate(this.addToDate(new Date(), { years: 1 }), 'yyyy/MM/dd');
    }, // validEmployeeStartDate()

    validEmployeeEndDate(d) {
      // Only allow dates after Emplyee start date
      return d > this.formatDate(this.editStaffMember.employeeStartDate.toDate(), 'yyyy/MM/dd');
    }, // validEmployeeEndDate()

    getDataFieldOptions(dataField) {
      return chain(dataField)
        .get('options')
        .map((option) => ({ value: option, label: option }))
        .value();
    }, // getDataFieldOptions()

    getStaffMemberData(dataField) {
      let defaultValue = null;
      switch (dataField.type) {
        case 'yesNo': {
          defaultValue = null;
          break;
        }
        case 'select': {
          defaultValue = dataField.allowMultipleOptions ? [] : '';
          break;
        }
        default:
      }
      return get(this.editStaffMember, ['customData', dataField.id], defaultValue);
    }, // getStaffMemberData()

    setStaffMemberData(dataFieldId, value) {
      this.editStaffMember.customData = {
        ...this.editStaffMember.customData,
        [dataFieldId]: value !== undefined ? value : null,
      };
    }, // setStaffMemberData()

    onStaffMemberDataSectionShown(section) {
      if (section?.id !== this.staffSchedulingGroupsSection?.id) {
        return;
      }

      editUserProfileAnalytics.trackProfileSchedulingGroupsExpanded({
        targetUserId: this.user.id,
        source: this.source,
      });
    }, // onStaffMemberDataSectionShown()

    getFullName(firstName, lastName) {
      return chain([firstName, lastName])
        .map((name) => trim(name))
        .compact()
        .join(' ')
        .value();
    }, // getFullName

    getUnitChecked(unitId) {
      if (!this.userBindsToUnits) {
        return false;
      }
      if (chain(this.editUser).get('units').includes(unitId).value()) {
        return true;
      }
      // Return indeterminate if some clients within the unit are checked
      return (
        chain(this.unitsWithClients)
          .find({ id: unitId })
          .get('clients')
          .intersection(this.selectedClients)
          .isEmpty()
          .value())
        ? false
        : null;
    }, // getUnitChecked()

    onCopySupportInfo() {
      copySupportInfoToClipboard({
        'target user': this.editUser?.userId,
        'staff profile id': this.editUser?.staffMember,
      });
    },

    async uniqueEmail(val) {
      if (this.user.email === val) {
        return true;
      }

      const { data } = await callFirebaseFunction('checkEmailUsageInUsersAndInvitations', { emails: [val] });

      if (data.exists) {
        return this.$t('users.modals.emailAlreadyInUseDialog.userAlreadyExistsTooltip');
      }

      return true;
    }, // uniqueEmail()

    onUpgradeToPremium() {
      this.editStaffMember.status = 'active';
    }, // onUpgradeToPremium()

    onDowngradeToStandard() {
      this.editStaffMember.status = 'inactive';
    }, // onDowngradeToStandard()

    onEnableUser() {
      if (!this.isExistingUser) {
        return;
      }

      this.confirmDialog(
        this.$t('common.dialogTitleConfirm'),
        this.$t('settings.users.dialogEnableUserMessage', { name: this.editUser.name }),
        async () => {
          const usersDb = new UsersDB();
          try {
            await usersDb.enableUser(this.editUser.userId);

            Object.assign(this.editUser, { status: 'active' });
            this.notifySuccess(this.$t('settings.users.notifyEnableUserSuccess', { name: this.editUser.name }));
          } catch (err) {
            this.notifyNegative(err.message);
          }
        },
      );
    }, // onEnableUser()

    async getAdminMFAStatus() {
      if (!this.user.id) {
        return;
      }

      const usersDb = new UsersDB();
      const user = await usersDb.read(this.user.id);

      if (!user) {
        return false;
      }

      return user.admin && user.requireMFA;
    }, // getAdminMFAStatus
  },

};
</script>
<style lang="scss" scoped>
.user-editor {
  min-width:75vw;
}

.staff-avatar-actions {
  position: absolute;
  top: -8px;
  right: -20px;
  z-index: 10;
}

.staff-photo-overlay {
  background-color: rgba(0, 0, 0, .5);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
  }
}

.main-fields {
  :deep(.q-field__control) {
    height: 100%;
  }
}

</style>
